import cn from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import SwiperCore from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import { Controller } from 'swiper/modules'
import { Swiper, SwiperSlide, useSwiperSlide } from 'swiper/react'

import { Text } from '@ds'
import {
  GalaxyButton,
  getTextWidthInRem,
  LockIcon,
  MarkIcon,
  Spiral,
  SwiperArrows,
  useSpirals,
  useUserSpiral,
  webApp,
} from '@shared'
import { useIsUserProfileFetching, useUserActions } from '@store'

import { useUpdateUserSpiralSkin } from '../../hooks'
import styles from './SpiralSelector.module.scss'

SwiperCore.use([Controller])

const hapticFeedback = webApp?.HapticFeedback

const BORDER_SLIDE_OFFSET = 16

const SpiralSlideContent = ({ src }: { src: string }) => {
  const { isActive, isNext, isPrev } = useSwiperSlide()

  const isAnimated = isActive || isNext || isPrev
  return <Spiral isAnimated={isAnimated} src={src} shouldPlay={isActive} />
}

const TextSlideContent = ({ text }: { text: string }) => {
  return (
    <Text type="h1" align="center" className={styles.testSlideTitle}>
      {text}
    </Text>
  )
}

export const SpiralSelector = () => {
  const [spiralSwiper, setSpiralSwiper] = useState<SwiperType | null>(null)
  const [textSwiper, setTextSwiper] = useState<SwiperType | null>(null)
  const [textContainerWidth, setTextContainerWidth] = useState(0)
  const [currentSpiral, setCurrentSpiral] = useState<string | null>(null)
  const { spiral: userSpiral } = useUserSpiral()
  const { spirals } = useSpirals()
  const { fetchProfile } = useUserActions()
  const isProfileFetching = useIsUserProfileFetching()

  const { handleUpdateUserSpiralSkin, loading: isUpdatingSpiral } =
    useUpdateUserSpiralSkin({
      onSuccess: () => {
        fetchProfile()
      },
    })

  const initialSpiral = useMemo(() => {
    if (!userSpiral) {
      return 0
    }

    const index = spirals.findIndex((item) => item.id === userSpiral.id)
    return index === -1 ? 0 : index
  }, [userSpiral])

  const isEquipped = useMemo(() => {
    return userSpiral?.id === currentSpiral
  }, [userSpiral, currentSpiral])

  const isAvailable = useMemo(() => {
    return spirals.find((item) => item.id === currentSpiral)?.isAvailable
  }, [currentSpiral])

  const isEquipButtonDisabled = useMemo(() => {
    return isUpdatingSpiral || isProfileFetching || !isAvailable || isEquipped
  }, [isUpdatingSpiral, isProfileFetching, isAvailable, isEquipped])

  const handleTextSwiperPrev = () => {
    if (!textSwiper) {
      return
    }

    hapticFeedback?.impactOccurred('medium')
    textSwiper.slidePrev()
  }
  const handleTextSwiperNext = () => {
    if (!textSwiper) {
      return
    }

    hapticFeedback?.impactOccurred('medium')
    textSwiper?.slideNext()
  }

  const handleSpiralSwiperChange = useCallback(
    (swiper: SwiperType) => {
      const activeIndex = swiper.realIndex
      const selectedSpiral = spirals[activeIndex].id
      setCurrentSpiral(selectedSpiral)
    },
    [setCurrentSpiral, spirals]
  )

  const handleEquipSpiral = useCallback(() => {
    if (isEquipButtonDisabled || !currentSpiral) {
      return
    }

    handleUpdateUserSpiralSkin(currentSpiral)
  }, [handleUpdateUserSpiralSkin, currentSpiral])

  const renderIcon = useCallback(() => {
    const offset = isAvailable ? 20 : 12

    const iconStyle = {
      left: `calc(50% + ${textContainerWidth / 2}rem - ${offset}rem) `,
    }
    if (isEquipped) {
      return <MarkIcon className={styles.textSlideIcon} style={iconStyle} />
    }

    if (!isAvailable) {
      return <LockIcon className={styles.textSlideIcon} style={iconStyle} />
    }

    return null
  }, [isEquipped, textContainerWidth, isAvailable])

  return (
    <div className={styles.container}>
      <GalaxyButton
        withGlow={false}
        className={cn(styles.equipButton, {
          [styles.disabled]: isEquipButtonDisabled,
        })}
        onClick={handleEquipSpiral}
        disabled={isEquipButtonDisabled}
      >
        <Text type="body" center className={styles.equipButton__text}>
          {isUpdatingSpiral || isProfileFetching ? 'Equipping...' : 'Equip'}
        </Text>
      </GalaxyButton>
      <Swiper
        centeredSlides
        loop
        initialSlide={initialSpiral}
        onSwiper={setSpiralSwiper}
        slidesPerView="auto"
        allowTouchMove={false}
        watchSlidesProgress
        noSwiping
        noSwipingClass="swiper-no-swiping"
        className={styles.swiper}
        onSlideChange={handleSpiralSwiperChange}
      >
        {spirals.map((spiral) => (
          <SwiperSlide className={styles.spiralSlide} key={spiral.id}>
            <SpiralSlideContent src={spiral.appFiles || ''} />
          </SwiperSlide>
        ))}
      </Swiper>
      {renderIcon()}
      <div className={styles.textSliderContainer}>
        <div
          className={styles.textSlideBackground}
          style={{ width: `${textContainerWidth}rem` }}
        />
        <Swiper
          grabCursor
          initialSlide={initialSpiral}
          onSwiper={setTextSwiper}
          centeredSlides
          loop
          slidesPerView="auto"
          onTouchStart={() => hapticFeedback?.impactOccurred('medium')}
          onSlideChange={(swiper) => {
            const activeElement = swiper.slides[swiper.activeIndex]
            const style = getComputedStyle(activeElement)

            const textWidth = getTextWidthInRem(
              activeElement.textContent!,
              style.font
            )

            setTextContainerWidth(textWidth + BORDER_SLIDE_OFFSET)
          }}
          watchSlidesProgress
          controller={{ control: spiralSwiper }}
          className={styles.textSwiper}
        >
          {spirals.map((spiral) => (
            <SwiperSlide
              className={cn(styles.textSlide, {
                [styles.disabled]: !spiral.isAvailable,
              })}
              key={spiral.id}
            >
              <TextSlideContent text={spiral.name} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className={cn(styles.textSlideBorder, {
            [styles.disabled]: !isAvailable,
          })}
          style={{ width: `${textContainerWidth}rem` }}
        />
      </div>
      <div className={styles.title}>
        <Text align="center" className={styles.title__subtitle}>
          Choose your own
        </Text>
        <Text align="center" type="h1" className={styles.title__value}>
          Spiral skin
        </Text>
      </div>
      <SwiperArrows
        className={styles.swiperArrows}
        onClockPrev={handleTextSwiperPrev}
        onClickNext={handleTextSwiperNext}
      />
    </div>
  )
}
